import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../lib/database.types';
import { softDelete } from '../../lib/softDelete';
import { supabase } from '../../lib/supabase';

type LeadSourceRow = Database['public']['Tables']['lead_sources']['Row'];
type LeadSourceInsert = Database['public']['Tables']['lead_sources']['Insert'];
type LeadSourceUpdate = Database['public']['Tables']['lead_sources']['Update'];

export type SourceRow = Pick<
  LeadSourceRow,
  'id' | 'name' | 'slug' | 'utm_source' | 'color' | 'sort_order' | 'created_at' | 'updated_at'
>;

export interface SourceInput {
  name: string;
  slug: string;
  utm_source: string | null;
  color: string | null;
  sort_order: number;
}

export const catalogSourcesQueryKey = (orgId: string | null) => ['catalog-sources', orgId] as const;

async function fetchSources(orgId: string): Promise<SourceRow[]> {
  const { data, error } = await supabase
    .from('lead_sources')
    .select('id, name, slug, utm_source, color, sort_order, created_at, updated_at')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data;
}

export function useCatalogSources(orgId: string | null) {
  return useQuery({
    queryKey: catalogSourcesQueryKey(orgId),
    queryFn: () => (orgId ? fetchSources(orgId) : Promise.resolve<SourceRow[]>([])),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

function emptyOrNull(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function useUpsertSource(orgId: string | null, userId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: SourceInput }) => {
      if (!orgId || !userId) throw new Error('Missing source context');

      if (id) {
        const payload: LeadSourceUpdate = {
          name: values.name.trim(),
          slug: values.slug.trim(),
          utm_source: emptyOrNull(values.utm_source),
          color: emptyOrNull(values.color),
          sort_order: values.sort_order,
        };
        const { error } = await supabase.from('lead_sources').update(payload).eq('id', id);
        if (error) throw error;
        return;
      }

      const payload: LeadSourceInsert = {
        organization_id: orgId,
        name: values.name.trim(),
        slug: values.slug.trim(),
        utm_source: emptyOrNull(values.utm_source),
        color: emptyOrNull(values.color),
        sort_order: values.sort_order,
        created_by: userId,
      };
      const { error } = await supabase.from('lead_sources').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: catalogSourcesQueryKey(orgId) });
      await queryClient.invalidateQueries({ queryKey: ['lead-sources', orgId] });
    },
  });
}

export function useSoftDeleteSource(orgId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await softDelete('lead_sources', id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: catalogSourcesQueryKey(orgId) });
      await queryClient.invalidateQueries({ queryKey: ['lead-sources', orgId] });
    },
  });
}
